import { FC, ReactNode, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { GridRenderCellParams } from '@mui/x-data-grid';
import { RootState } from 'store';
import { updatedUserRole } from 'store/features/authentication';
import styles from './PersonRoleCell.module.scss';
import { activateSnackbar } from 'store/features/ui';

interface PersonRoleCellProps extends GridRenderCellParams {
    onChange?: (uid: string, newRole: number) => void;
    isDisabled?: boolean;
}

const PersonRoleCell: FC<PersonRoleCellProps> = (
    props: PersonRoleCellProps
) => {
    const [roleId, setRoleId] = useState(props.value ?? 3);
    const authentication = useSelector(
        (state: RootState) => state.authentication
    );
    const dispatch = useDispatch();

    const selectedNewValue = (
        event: SelectChangeEvent<number>,
        _child: ReactNode
    ) => {
        if (authentication.user !== null && authentication.user.uid !== null) {
            const eventTarget = event.target.value;

            const newRoleId =
                typeof eventTarget === 'number'
                    ? eventTarget
                    : parseInt(eventTarget);

            const activeUsersRoleId = authentication.user.protected?.roleId;

            // Account owners and power users may change roles. If you are a power user you may not change a role to account owner. Admin users with edit users privs may also change roles.
            const isAccountOwner = activeUsersRoleId === 0;
            const isPowerUser = activeUsersRoleId === 1 && newRoleId !== 0;
            const isAdminUser =
                authentication.user.protected?.supplierId?.toString() === '0' &&
                authentication.user.protected.privs?.user_edit;

            const canChangeRole =
                activeUsersRoleId !== undefined
                    ? isAccountOwner || isPowerUser || isAdminUser
                    : false;

            if (canChangeRole) {
                dispatch(
                    updatedUserRole({
                        currentUserId: authentication.user.uid,
                        updatedUserId: props.row.uid,
                        newRoleId: newRoleId,
                        readOnly: !canChangeRole,
                    })
                );
                setRoleId(newRoleId);
                dispatch(
                    activateSnackbar({
                        message: 'User role successfully changed.',
                        severity: 'success',
                    })
                );
            } else {
                dispatch(
                    activateSnackbar({
                        message:
                            'Account owners and power users may change roles. If you are a power user you may not change a role to account owner.',
                        severity: 'error',
                    })
                );
            }
        }
    };
    return (
        <div
            className={styles['person-role-cell']}
            data-testid="PersonRoleCell"
        >
            <Select
                className={styles['role-select']}
                value={roleId === -1 ? 3 : roleId}
                onChange={selectedNewValue}
                disabled={props.isDisabled}
            >
                <MenuItem value={0}>Account Owner</MenuItem>
                <MenuItem value={1}>Power User</MenuItem>
                <MenuItem value={2}>User</MenuItem>
                <MenuItem value={3}>Limited Access</MenuItem>
            </Select>
        </div>
    );
};

export default PersonRoleCell;
